import { FocusEvent } from "react";

/**
 * Copies text to the clipboard.
 *
 * @param text - The text to be copied.
 */
export const copyToClipboard = async (text: string, clipboard: Clipboard = navigator.clipboard) => {
  try {
    await clipboard.writeText(text);
  } catch (error) {
    console.error(error);
    fallbackCopyToClipboard(text);
  }
};

function fallbackCopyToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;

  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    const msg = successful ? "successful" : "unsuccessful";
    console.error(`Fallback: Copying text command was ${msg}`);
  } catch (err) {
    console.error(err);
  } finally {
    document.body.removeChild(textArea);
  }
}

/**
 * Returns the nearest scrollable parent element of the given element.
 * If no scrollable parent is found, the function returns the root element.
 *
 * @param element - The element for which to find the nearest scrollable parent.
 * @returns The nearest scrollable parent element or the root element.
 */
export function getNearestScrollableParent(element: HTMLElement | null) {
  if (!element) return undefined;

  let parent = element as HTMLElement | null;

  while (parent) {
    if (parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    const style = window.getComputedStyle(parent);
    const overflow = style.getPropertyValue("overflow");
    const overflowY = style.getPropertyValue("overflow-y");

    if (
      overflow === "auto" ||
      overflow === "scroll" ||
      overflowY === "auto" ||
      overflowY === "scroll"
    ) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return document.documentElement;
}

/**
 * Requests the browser to schedule a callback to be executed during an idle period.
 * If the browser does not support `requestIdleCallback`, a fallback using `setTimeout` is used.
 *
 * @param cb - The callback function to be executed during an idle period.
 * @returns A handle that can be used to cancel the scheduled callback.
 */
export function requestIdleCallback(cb: () => void) {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    return window.requestIdleCallback(cb);
  }

  return setTimeout(cb, 1);
}

/**
 * Requests the browser to schedule a callback to be executed during an idle period.
 * If the browser does not support `requestIdleCallback`, a fallback using `setTimeout` is used.
 *
 * @param {event} - Event from input element.
 * @returns {inputRef} - RefObject form input element.
 */
export function onFocusWithoutScroll(
  event: FocusEvent<HTMLInputElement>,
  inputRef: React.RefObject<HTMLInputElement>
) {
  event.preventDefault(); // stop Add-in iframe scrolling, when input focus
  event.stopPropagation();
  setTimeout(() => inputRef.current?.focus(), 0);
}

/**
 * Gets file extension based on content-type.
 *
 * @param contentType - The content type from HTTP response.
 * @returns The file extension with dot prefix or empty string if not recognized.
 */
export function getFileExtensionFromContentType(contentType: string): string {
  if (contentType.includes("pdf")) {
    return ".pdf";
  } else if (contentType.includes("png")) {
    return ".png";
  } else if (contentType.includes("jpeg") || contentType.includes("jpg")) {
    return ".jpg";
  } else if (contentType.includes("gif")) {
    return ".gif";
  } else if (contentType.includes("movie") || contentType.includes("quicktime")) {
    return ".mov";
  } else if (contentType.includes("mp4")) {
    return ".mp4";
  } else if (contentType.includes("x-iwork-pages")) {
    return ".pages";
  } else if (contentType.includes("officedocument.wordprocessingml")) {
    return ".docx";
  } else if (contentType.includes("officedocument.spreadsheetml")) {
    return ".xlsx";
  } else if (contentType.includes("officedocument.presentationml")) {
    return ".pptx";
  } else if (contentType.includes("msword")) {
    return ".doc";
  } else if (contentType.includes("ms-powerpoint")) {
    return ".ppt";
  }
  return "";
}
